import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { AuditEntry } from '@/api/types';
import { Card } from '@/design-system';
import { now } from '@/lib/clock';
import { daysUntil, formatTime } from '@/lib/format';
import { LoadError } from '@/shell/LoadError';
import { orgActivityQuery } from './api';
import { useOrg } from './OrganizationPage';
import s from './organizations.module.css';

type Tv = (k: string, o?: Record<string, unknown>) => string;

/** «Сегодня, 14:32», «Вчера, 17:45», «27 окт». */
export function whenLabel(at: string, t: Tv, months: string[]): string {
  const days = -daysUntil(at, now());
  if (days <= 0) return t('activity.today', { time: formatTime(at) });
  if (days === 1) return t('activity.yesterday', { time: formatTime(at) });
  const d = new Date(at);
  const local = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Tashkent', day: 'numeric', month: 'numeric' }).formatToParts(d);
  const day = local.find((p) => p.type === 'day')?.value ?? '';
  const month = Number(local.find((p) => p.type === 'month')?.value ?? 1);
  return `${day} ${months[month - 1] ?? ''}`;
}

/** The event's sentence: a template per `entity__action` (dots as `_`), filled from `after`. */
export function activityText(e: AuditEntry, t: Tv): string {
  const after = e.after ?? {};
  const key = `${e.entity}__${e.action.replace(/\./g, '_')}`;
  const params = {
    ...after,
    channel_label: typeof after.channel === 'string' ? t(`activity.channel.${after.channel}`, { defaultValue: after.channel }) : '',
    period_label: typeof after.period === 'string' ? t(`activity.period.${after.period}`, { defaultValue: after.period }) : '',
  };
  return t(`activity.events.${key}`, { ...params, defaultValue: e.summary || t('activity.fallback', { entity: e.entity, action: e.action }) });
}

/** Активность (DESIGN_SPEC §4.3): the organization's audit rows as a timeline (`time · dot · event`). */
export function ActivityTab() {
  const { org } = useOrg();
  const { t } = useTranslation('organizations');
  const { t: tc } = useTranslation('common');
  const tv = t as unknown as Tv;
  const months = (tc as TFunction<'common'>)('months_short_genitive', { returnObjects: true }) as string[];
  const q = useQuery(orgActivityQuery(org.id));
  if (q.isError) return <LoadError error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return null;
  return (
    <Card padding="none" className={s.activity}>
      {q.data.length === 0 && <div className={s.activityEmpty}>{t('activity.empty')}</div>}
      {q.data.map((e) => (
        <div key={e.id} className={s.event}>
          <span className={s.eventTime}>{whenLabel(e.created_at, tv, months)}</span>
          <span className={s.eventDot} />
          <span>{activityText(e, tv)}</span>
        </div>
      ))}
    </Card>
  );
}
